import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { axios } from '../../libs/axios';
import { User } from '../../states/actions';
import { useTranslation } from '../../i18n/I18nContext';
import logger from '../../utils/logger';
import { ClientVerifyFieldForm } from './ClientVerifyFieldForm';
import {
	capitalizeName,
	clientToForm,
	validateCompanyRegCode,
	validateEmail,
	validateName,
	validatePID,
	validatePhone,
} from '../../utils/clientValidation';

function formatProjectTypeChip(projectType, t) {
	const map = {
		lawyer: t('projects.typeLawyer'),
		admin: t('projects.typeAdmin'),
		general: t('projects.typeGeneralBooking'),
		'General Booking': t('projects.typeGeneralBooking'),
		inquiry: t('common.inquiry'),
		'initiated inquiry': t('common.typeInitiatedInquiry'),
	};
	const label = map[projectType] || projectType || '';
	return String(label).toLowerCase();
}

function LockedFieldRow({
	field,
	label,
	client,
	form,
	projectId,
	panelOpen,
	onOpenPanel,
	onVerified,
	t,
}) {
	const isHidden = field === 'email' ? client?.email_locked : client?.pid_hidden;
	const verifyLocked = field === 'email' ? client?.email_verify_locked : client?.pid_verify_locked;
	const attemptsLeft = field === 'email' ? client?.email_attempts_left : client?.pid_attempts_left;

	if (!isHidden) {
		const value = form[field];
		return (
			<div className="client-modal-row">
				<span className="client-modal-label">{label}</span>
				<span className="client-modal-value">
					{field === 'email' && value ? (
						<a href={`mailto:${value}`}>{value}</a>
					) : (
						value || '—'
					)}
				</span>
			</div>
		);
	}

	return (
		<>
			<div className="client-modal-row">
				<span className="client-modal-label client-modal-label--locked">
					<i className="fa-solid fa-lock" aria-hidden="true" />
					{label}
				</span>
				<span className="client-modal-value client-modal-value--locked">
					{!panelOpen && (
						<>
							<span className="client-modal-mask" aria-hidden="true">
								{field === 'email' ? '••••••••••' : '•••••••••••'}
							</span>
							{!verifyLocked && (
								<button
									type="button"
									className="button client-modal-open-btn"
									onClick={() => onOpenPanel(field)}
								>
									{t('project.unlockField')}
								</button>
							)}
						</>
					)}
				</span>
			</div>
			{panelOpen && (
				<ClientVerifyFieldForm
					variant="panel"
					clientId={client._id}
					projectId={projectId}
					field={field}
					locked={verifyLocked}
					attemptsLeft={attemptsLeft}
					onVerified={async () => {
						await onVerified?.();
						onOpenPanel(null);
					}}
				/>
			)}
		</>
	);
}

export function ClientEditModal({
	client,
	open,
	onClose,
	onSaved,
	onVerified,
	projectId,
	projectType,
	projectNo,
}) {
	const user = useSelector(User.get());
	const { t } = useTranslation();
	const [form, setForm] = useState(clientToForm(client));
	const [emailError, setEmailError] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [pidError, setPidError] = useState('');
	const [nameError, setNameError] = useState('');
	const [companyRegError, setCompanyRegError] = useState('');
	const [generalError, setGeneralError] = useState('');
	const [saving, setSaving] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [openVerifyPanel, setOpenVerifyPanel] = useState(null);

	const isLawyer = user?.role === 'lawyer';
	const isLawyerProject = projectType === 'lawyer';

	useEffect(() => {
		if (open && client) {
			setForm(clientToForm(client));
			setEmailError('');
			setPhoneError('');
			setPidError('');
			setNameError('');
			setCompanyRegError('');
			setGeneralError('');
			setIsEditing(false);
			setOpenVerifyPanel(null);
		}
	}, [open, client]);

	const adminFields = useMemo(() => ([
		{ name: 'firstname', label: t('clients.fieldFirstName'), width: 'col-half' },
		{ name: 'lastname', label: t('clients.fieldLastName'), width: 'col-half' },
		{ name: 'email', label: t('clients.fieldEmail'), width: 'col-half' },
		{ name: 'phone', label: t('clients.fieldPhone'), width: 'col-half' },
		{ name: 'pid', label: t('clients.fieldPersonalId'), width: 'col-full' },
		{
			name: 'type',
			label: t('clients.fieldClientType'),
			width: 'col-full',
			type: 'select',
			options: [
				{ value: 'person', label: t('clients.fieldPerson') },
				{ value: 'business', label: t('clients.fieldBusiness') },
			],
		},
		{ name: 'business_name', label: t('clients.fieldBusinessName'), width: 'col-full', showWhen: 'business' },
		{ name: 'business_address', label: t('clients.fieldBusinessAddress'), width: 'col-full', showWhen: 'business' },
		{ name: 'business_reg_code', label: t('clients.fieldRegistrationCode'), width: 'col-half', showWhen: 'business' },
	]), [t]);

	const handleInputChange = (fieldName, value) => {
		let processedValue = value;
		if (fieldName === 'firstname' || fieldName === 'lastname') {
			processedValue = capitalizeName(value);
		}
		setForm((prev) => ({ ...prev, [fieldName]: processedValue }));
	};

	const handleInputBlur = (fieldName, value) => {
		if (fieldName === 'firstname' || fieldName === 'lastname') {
			const trimmedValue = value.trim();
			if (trimmedValue !== value) {
				setForm((prev) => ({ ...prev, [fieldName]: trimmedValue }));
			}
		}
	};

	const validateLawyerEditForm = (trimmedForm) => {
		let hasValidationErrors = false;

		const firstNameValidation = validateName(trimmedForm.firstname, t);
		if (!firstNameValidation.isValid) {
			setNameError(firstNameValidation.error);
			hasValidationErrors = true;
		}

		const lastNameValidation = validateName(trimmedForm.lastname, t);
		if (!lastNameValidation.isValid) {
			setNameError(lastNameValidation.error);
			hasValidationErrors = true;
		}

		const phoneValidation = validatePhone(trimmedForm.phone, t);
		if (!phoneValidation.isValid) {
			setPhoneError(phoneValidation.error);
			hasValidationErrors = true;
		}

		const pidValidation = validatePID(trimmedForm.pid, t);
		if (!pidValidation.isValid) {
			setPidError(pidValidation.error);
			hasValidationErrors = true;
		}

		if (trimmedForm.type === 'business' && trimmedForm.business_reg_code) {
			const regCodeValidation = validateCompanyRegCode(trimmedForm.business_reg_code, t);
			if (!regCodeValidation.isValid) {
				setCompanyRegError(regCodeValidation.error);
				hasValidationErrors = true;
			}
		}

		return !hasValidationErrors;
	};

	const validateAdminForm = (trimmedForm) => {
		let hasValidationErrors = false;

		const firstNameValidation = validateName(trimmedForm.firstname, t);
		if (!firstNameValidation.isValid) {
			setNameError(firstNameValidation.error);
			hasValidationErrors = true;
		}

		const lastNameValidation = validateName(trimmedForm.lastname, t);
		if (!lastNameValidation.isValid) {
			setNameError(lastNameValidation.error);
			hasValidationErrors = true;
		}

		const emailValidation = validateEmail(trimmedForm.email, t);
		if (!emailValidation.isValid) {
			setEmailError(emailValidation.error);
			hasValidationErrors = true;
		}

		const phoneValidation = validatePhone(trimmedForm.phone, t);
		if (!phoneValidation.isValid) {
			setPhoneError(phoneValidation.error);
			hasValidationErrors = true;
		}

		const pidValidation = validatePID(trimmedForm.pid, t);
		if (!pidValidation.isValid) {
			setPidError(pidValidation.error);
			hasValidationErrors = true;
		}

		if (trimmedForm.type === 'business' && trimmedForm.business_reg_code) {
			const regCodeValidation = validateCompanyRegCode(trimmedForm.business_reg_code, t);
			if (!regCodeValidation.isValid) {
				setCompanyRegError(regCodeValidation.error);
				hasValidationErrors = true;
			}
		}

		return !hasValidationErrors;
	};

	const runBackendDuplicateChecks = async (trimmedForm, clientId) => {
		if (user?.role !== 'admin') {
			return true;
		}

		if (trimmedForm.email?.trim()) {
			const data = await axios.get(`/clients/check-email?email=${encodeURIComponent(trimmedForm.email)}&excludeId=${clientId}`);
			if (data?.exists) {
				setEmailError(t('clients.emailInUse'));
				return false;
			}
		}

		if (trimmedForm.phone?.trim()) {
			const data = await axios.get(`/clients/check-phone?phone=${encodeURIComponent(trimmedForm.phone)}&excludeId=${clientId}`);
			if (data?.exists) {
				setPhoneError(t('clients.phoneInUse'));
				return false;
			}
		}

		if (trimmedForm.pid?.trim()) {
			const data = await axios.get(`/clients/check-pid?pid=${encodeURIComponent(trimmedForm.pid)}&excludeId=${clientId}`);
			if (data?.exists) {
				setPidError(t('clients.pidInUse'));
				return false;
			}
		}

		return true;
	};

	const handleLawyerSave = async (e) => {
		e.preventDefault();
		if (!client?._id || !isLawyerProject) return;

		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');

		const trimmedForm = {
			...form,
			firstname: form.firstname ? form.firstname.trim() : '',
			lastname: form.lastname ? form.lastname.trim() : '',
		};
		setForm(trimmedForm);

		if (!validateLawyerEditForm(trimmedForm)) {
			return;
		}

		try {
			setSaving(true);
			const result = await axios.put(`/clients/${client._id}`, { ...trimmedForm, projectId });
			if (result?.status && result.status >= 400) {
				setGeneralError(result.message || result.error || t('common.unknownError'));
				return;
			}

			const freshClient = await axios.get(`/clients/${client._id}`);
			if (freshClient?.status && freshClient.status >= 400) {
				setGeneralError(freshClient.message || freshClient.error || t('common.unknownError'));
				return;
			}

			setForm(clientToForm(freshClient));
			setIsEditing(false);
			onSaved?.(freshClient);
		} catch (error) {
			logger.error('Error updating client:', error);
			setGeneralError(error?.response?.data?.error || error.message || t('common.unknownError'));
		} finally {
			setSaving(false);
		}
	};

	const handleAdminSubmit = async (e) => {
		e.preventDefault();
		if (!client?._id) return;

		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');

		const trimmedForm = {
			...form,
			firstname: form.firstname ? form.firstname.trim() : '',
			lastname: form.lastname ? form.lastname.trim() : '',
		};
		setForm(trimmedForm);

		if (!validateAdminForm(trimmedForm)) {
			return;
		}

		try {
			setSaving(true);

			const duplicateOk = await runBackendDuplicateChecks(trimmedForm, client._id);
			if (!duplicateOk) {
				return;
			}

			const result = await axios.put(`/clients/${client._id}`, trimmedForm);
			if (result?.status && result.status >= 400) {
				setGeneralError(result.message || result.error || t('common.unknownError'));
				return;
			}

			const freshClient = await axios.get(`/clients/${client._id}`);
			if (freshClient?.status && freshClient.status >= 400) {
				setGeneralError(freshClient.message || freshClient.error || t('common.unknownError'));
				return;
			}

			onSaved?.(freshClient);
			onClose();
		} catch (error) {
			logger.error('Error updating client:', error);
			setGeneralError(error?.response?.data?.error || error.message || t('common.unknownError'));
		} finally {
			setSaving(false);
		}
	};

	const handleCancelEdit = () => {
		setForm(clientToForm(client));
		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');
		setIsEditing(false);
	};

	if (!open || !client) {
		return null;
	}

	const hasHiddenFields = client.email_locked;
	const showProtectedNote = hasHiddenFields && !isLawyerProject;
	const chipLabel = projectNo
		? `${formatProjectTypeChip(projectType, t)} · #${projectNo}`
		: formatProjectTypeChip(projectType, t);

	const renderLawyerViewRow = (label, value, options = {}) => (
		<div className={`client-modal-row${options.last ? ' client-modal-row--last' : ''}`}>
			<span className="client-modal-label">{label}</span>
			<span className={`client-modal-value${options.muted ? ' client-modal-value--muted' : ''}`}>
				{value || '—'}
			</span>
		</div>
	);

	const renderLawyerEditInput = (fieldName, label, options = {}) => (
		<div className={`client-modal-row client-modal-row--edit${options.last ? ' client-modal-row--last' : ''}`}>
			<span className="client-modal-label">{label}</span>
			<span className="client-modal-value client-modal-value--input">
				{options.type === 'select' ? (
					<select
						value={form[fieldName]}
						onChange={(e) => handleInputChange(fieldName, e.target.value)}
						required
					>
						{options.options.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				) : (
					<input
						type="text"
						value={form[fieldName]}
						onChange={(e) => handleInputChange(fieldName, e.target.value)}
						onBlur={(e) => handleInputBlur(fieldName, e.target.value)}
						className={
							((fieldName === 'firstname' || fieldName === 'lastname') && nameError)
							|| (fieldName === 'phone' && phoneError)
							|| (fieldName === 'pid' && pidError)
							|| (fieldName === 'business_reg_code' && companyRegError) ? 'error' : ''
						}
					/>
				)}
			</span>
		</div>
	);

	if (isLawyer) {
		return (
			<div className="modal client client-modal--lawyer" onClick={onClose}>
				<div className="modal-content client-modal-content--lawyer" onClick={(e) => e.stopPropagation()}>
					<div className="modal-header client-modal-header--lawyer">
						<span className="modalclose" onClick={onClose}>&times;</span>
						<h2>{t('clients.viewTitle')}</h2>
						{chipLabel && <span className="client-modal-chip">{chipLabel}</span>}
					</div>

					<div className="client-modal-body">
						{showProtectedNote && !isEditing && (
							<div className="client-modal-note">
								<i className="fa-solid fa-lock" aria-hidden="true" />
								<p>{t('project.clientModalProtectedNote')}</p>
							</div>
						)}

						{isEditing ? (
							<form className="client-modal-form client-modal-form--edit" onSubmit={handleLawyerSave}>
								{renderLawyerEditInput('firstname', t('clients.fieldFirstName'))}
								{renderLawyerEditInput('lastname', t('clients.fieldLastName'))}
								<div className="client-modal-row client-modal-row--edit">
									<span className="client-modal-label client-modal-label--locked">
										<i className="fa-solid fa-lock" aria-hidden="true" />
										{t('clients.fieldEmail')}
									</span>
									<span className="client-modal-value client-modal-value--muted">
										{client.email_locked ? (
											<span className="client-modal-mask" aria-hidden="true">••••••••••</span>
										) : (
											form.email || '—'
										)}
									</span>
								</div>
								{renderLawyerEditInput('phone', t('clients.fieldPhone'))}
								{renderLawyerEditInput('pid', t('clients.fieldPersonalId'))}
								{renderLawyerEditInput('type', t('clients.fieldClientType'), {
									type: 'select',
									options: [
										{ value: 'person', label: t('clients.fieldPerson') },
										{ value: 'business', label: t('clients.fieldBusiness') },
									],
								})}
								{form.type === 'business' && (
									<>
										{renderLawyerEditInput('business_name', t('clients.fieldBusinessName'))}
										{renderLawyerEditInput('business_address', t('clients.fieldBusinessAddress'))}
										{renderLawyerEditInput('business_reg_code', t('clients.fieldRegistrationCode'))}
									</>
								)}
								<p className="client-modal-email-hint">{t('project.clientModalEmailHint')}</p>
								{(nameError || phoneError || pidError || companyRegError || generalError) && (
									<div className="client-modal-verify-error">
										{nameError || phoneError || pidError || companyRegError || generalError}
									</div>
								)}
							</form>
						) : (
							<div className="client-modal-form client-modal-form--view">
								{!isLawyerProject ? (
									renderLawyerViewRow(
										t('clients.fieldFullName'),
										`${form.firstname || ''} ${form.lastname || ''}`.trim(),
									)
								) : (
									<>
										{renderLawyerViewRow(t('clients.fieldFirstName'), form.firstname)}
										{renderLawyerViewRow(t('clients.fieldLastName'), form.lastname)}
									</>
								)}
								{renderLawyerViewRow(t('clients.fieldPhone'), form.phone)}
								<LockedFieldRow
									field="email"
									label={t('clients.fieldEmail')}
									client={client}
									form={form}
									projectId={projectId}
									panelOpen={openVerifyPanel === 'email'}
									onOpenPanel={setOpenVerifyPanel}
									onVerified={onVerified}
									t={t}
								/>
								{renderLawyerViewRow(t('clients.fieldPersonalId'), form.pid)}
								{renderLawyerViewRow(
									t('clients.fieldClientType'),
									form.type === 'business' ? t('clients.fieldBusiness') : t('clients.fieldPerson'),
									{ last: form.type !== 'business' },
								)}
								{form.type === 'business' && (
									<>
										{renderLawyerViewRow(t('clients.fieldBusinessName'), form.business_name)}
										{renderLawyerViewRow(t('clients.fieldBusinessAddress'), form.business_address)}
										{renderLawyerViewRow(t('clients.fieldRegistrationCode'), form.business_reg_code, { last: true })}
									</>
								)}
							</div>
						)}
					</div>

					<div className="client-modal-footer">
						{isEditing && (
							<span className="client-modal-footer-note">{t('project.clientModalFooterChangesLogged')}</span>
						)}
						<div className="client-modal-footer-actions">
							{isEditing ? (
								<>
									<button type="button" className="button" onClick={handleCancelEdit} disabled={saving}>
										{t('common.cancel')}
									</button>
									<button type="button" className="button button-blue" onClick={handleLawyerSave} disabled={saving}>
										{saving ? t('common.saving') : t('common.save')}
									</button>
								</>
							) : (
								<>
									{isLawyerProject && (
										<button type="button" className="button" onClick={() => setIsEditing(true)}>
											{t('common.edit')}
										</button>
									)}
									<button type="button" className="button" onClick={onClose}>
										{t('common.close')}
									</button>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="modal client" onClick={onClose}>
			<div className="modal-content" onClick={(e) => e.stopPropagation()}>
				<div className="modal-header">
					<span className="modalclose" onClick={onClose}>&times;</span>
					<h2>{t('clients.editTitle')}</h2>
				</div>
				<div className="modal-form">
					<form onSubmit={handleAdminSubmit}>
						{adminFields.map((field) => {
							if (field.showWhen && form.type !== field.showWhen) {
								return null;
							}

							return (
								<div key={field.name} className={`form-row ${field.width} row-${field.name}`}>
									{field.type === 'select' ? (
										<>
											<label>{field.label}:</label>
											<select
												value={form[field.name]}
												onChange={(e) => handleInputChange(field.name, e.target.value)}
												required
											>
												{field.options.map((option) => (
													<option key={option.value} value={option.value}>
														{option.label}
													</option>
												))}
											</select>
										</>
									) : (
										<>
											<input
												placeholder={field.label}
												required={['firstname', 'lastname', 'email'].includes(field.name)}
												type={field.name === 'email' ? 'email' : 'text'}
												value={form[field.name]}
												onChange={(e) => handleInputChange(field.name, e.target.value)}
												onBlur={(e) => handleInputBlur(field.name, e.target.value)}
												className={
													(field.name === 'email' && emailError)
													|| (field.name === 'phone' && phoneError)
													|| (field.name === 'pid' && pidError)
													|| ((field.name === 'firstname' || field.name === 'lastname') && nameError)
													|| (field.name === 'business_reg_code' && companyRegError) ? 'error' : ''
												}
											/>
											{field.name === 'email' && emailError && (
												<div className="email-error-message">{emailError}</div>
											)}
											{field.name === 'phone' && phoneError && (
												<div className="email-error-message">{phoneError}</div>
											)}
											{field.name === 'pid' && pidError && (
												<div className="email-error-message">{pidError}</div>
											)}
											{(field.name === 'firstname' || field.name === 'lastname') && nameError && (
												<div className="email-error-message">{nameError}</div>
											)}
											{field.name === 'business_reg_code' && companyRegError && (
												<div className="email-error-message">{companyRegError}</div>
											)}
										</>
									)}
								</div>
							);
						})}

						{generalError && (
							<div className="form-row col-full">
								<div className="email-error-message" style={{ textAlign: 'center', marginTop: '10px' }}>
									{generalError}
								</div>
							</div>
						)}

						<div className="form-row col-full row-actions">
							<button type="button" className="button button-large" onClick={onClose} disabled={saving}>
								{t('common.cancel')}
							</button>
							<button type="submit" className="button button-large button-blue" disabled={saving}>
								{saving ? t('common.saving') : t('common.update')}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
